import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icon } from 'Components';
import {
	COMMIT_KEYS,
	DUPLICATE_FLASH_MS,
	EMAIL_PATTERN,
	EMAIL_SPLIT_PATTERN,
	ERROR_FLASH_MS
} from './EmailTagInput.constants';
import styles from './EmailTagInput.module.scss';

export const isValidEmail = (email: string) =>
	EMAIL_PATTERN.test(email.trim().toLowerCase());

const splitFragments = (raw: string) =>
	raw.split(EMAIL_SPLIT_PATTERN).filter(Boolean);

interface EmailTagInputProps {
	/** Controlled list. Leave undefined to let the field own its state. */
	value?: string[];
	/** Seeds the list while the field is uncontrolled. */
	defaultValue?: string[];
	onChange?: (emails: string[]) => void;
	placeholder?: string;
	id?: string;
	className?: string;
	disabled?: boolean;
}

/**
 * Email addresses entered as chips: type an address and Enter, Tab, comma or
 * space turns it into one. Anything that is not a valid address stays as
 * editable text instead of becoming a chip.
 */
const EmailTagInput: React.FC<EmailTagInputProps> = ({
	value,
	defaultValue,
	onChange,
	placeholder,
	id,
	className,
	disabled
}) => {
	const [internalEmails, setInternalEmails] = useState<string[]>(
		defaultValue ?? []
	);
	const [draft, setDraft] = useState('');
	const [isFocused, setIsFocused] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [flashedEmail, setFlashedEmail] = useState<string | null>(null);

	const inputRef = useRef<HTMLInputElement>(null);
	const errorTimer = useRef<number>();
	const flashTimer = useRef<number>();

	useEffect(
		() => () => {
			window.clearTimeout(errorTimer.current);
			window.clearTimeout(flashTimer.current);
		},
		[]
	);

	const emails = value ?? internalEmails;

	const applyChange = (next: string[]) => {
		if (value === undefined) {
			setInternalEmails(next);
		}
		onChange?.(next);
	};

	const flashError = () => {
		setHasError(true);
		window.clearTimeout(errorTimer.current);
		errorTimer.current = window.setTimeout(
			() => setHasError(false),
			ERROR_FLASH_MS
		);
	};

	const flashDuplicate = (email: string) => {
		setFlashedEmail(email);
		window.clearTimeout(flashTimer.current);
		flashTimer.current = window.setTimeout(
			() => setFlashedEmail(null),
			DUPLICATE_FLASH_MS
		);
	};

	/**
	 * Chips every valid address it is handed, flags the first address already
	 * in the list, and hands back whatever could not be chipped so it can stay
	 * in the input.
	 */
	const addEmails = (fragments: string[]) => {
		const added: string[] = [];
		const invalid: string[] = [];
		let duplicate: string | undefined;

		fragments.forEach((fragment) => {
			if (!isValidEmail(fragment)) {
				invalid.push(fragment);
				return;
			}

			const address = fragment.trim();
			const existing = [...emails, ...added].find(
				(email) => email.toLowerCase() === address.toLowerCase()
			);

			if (existing) {
				duplicate = duplicate ?? existing;
				return;
			}

			added.push(address);
		});

		if (added.length) {
			applyChange([...emails, ...added]);
		}
		if (duplicate) {
			flashDuplicate(duplicate);
		}
		if (invalid.length) {
			flashError();
		}

		return invalid.join(', ');
	};

	const commitDraft = () => setDraft(addEmails(splitFragments(draft)));

	const removeEmail = (email: string) => {
		applyChange(emails.filter((current) => current !== email));
		inputRef.current?.focus();
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		// Standard chip-input shorthand for "undo the last one".
		if (event.key === 'Backspace' && !draft && emails.length) {
			event.preventDefault();
			applyChange(emails.slice(0, -1));
			return;
		}

		if (!COMMIT_KEYS.includes(event.key)) {
			return;
		}

		if (!draft.trim()) {
			// Nothing to commit — Tab still moves focus on, the rest are noise.
			if (event.key !== 'Tab') {
				event.preventDefault();
			}
			return;
		}

		event.preventDefault();
		commitDraft();
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		const pasted = event.clipboardData.getData('text');
		if (!pasted) {
			return;
		}

		// Pasting onto a half-typed address should complete it, not split it.
		event.preventDefault();
		setDraft(addEmails(splitFragments(`${draft}${pasted}`)));
	};

	const handleBlur = () => {
		setIsFocused(false);
		commitDraft();
	};

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles['root--focused']]: isFocused,
					[styles['root--error']]: hasError,
					[styles['root--disabled']]: disabled
				},
				className
			)}
			onClick={() => inputRef.current?.focus()}
		>
			{emails.map((email) => (
				<span
					key={email}
					className={classNames(styles.chip, {
						[styles['chip--flash']]: flashedEmail === email
					})}
				>
					<span className={styles.chipLabel}>{email}</span>
					<button
						type="button"
						className={styles.remove}
						aria-label={`Remove ${email}`}
						disabled={disabled}
						onClick={(event) => {
							event.stopPropagation();
							removeEmail(email);
						}}
					>
						<Icon type="close" role="img" />
					</button>
				</span>
			))}

			<input
				ref={inputRef}
				id={id}
				type="email"
				autoComplete="off"
				className={styles.input}
				value={draft}
				disabled={disabled}
				aria-invalid={hasError}
				placeholder={emails.length ? undefined : placeholder}
				onChange={(event) => setDraft(event.target.value)}
				onKeyDown={handleKeyDown}
				onPaste={handlePaste}
				onFocus={() => setIsFocused(true)}
				onBlur={handleBlur}
			/>
		</div>
	);
};

export default EmailTagInput;
